'use client';

import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { cn } from '@/components/ui/utils';
import BlogCard from './BlogCard';
import type { BlogPost } from '../data/blogs';

interface BlogListClientProps {
  posts: BlogPost[];
  locale: string;
}

const POSTS_PER_PAGE = 6;

export default function BlogListClient({ posts, locale }: BlogListClientProps) {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  const filteredPosts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return posts;

    return posts.filter((post) =>
      [post.title, post.excerpt, post.category].join(' ').toLowerCase().includes(normalizedQuery),
    );
  }, [posts, query]);

  const pageCount = Math.max(1, Math.ceil(filteredPosts.length / POSTS_PER_PAGE));
  const currentPage = Math.min(page, pageCount);
  const visiblePosts = filteredPosts.slice(
    (currentPage - 1) * POSTS_PER_PAGE,
    currentPage * POSTS_PER_PAGE,
  );

  const goToPage = (nextPage: number) => {
    setPage(Math.min(pageCount, Math.max(1, nextPage)));
  };

  return (
    <>
      <div className="mb-8 flex justify-start sm:mb-10 sm:justify-end">
        <div className="relative w-full sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-600" />
          <Input
            type="search"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
            placeholder="Search"
            className="h-11 rounded-md border border-gray-300 pl-9 text-sm text-black shadow-none placeholder:text-black/45 focus-visible:ring-2 focus-visible:ring-[#ed1c22]/50 sm:h-10"
          />
        </div>
      </div>

      {visiblePosts.length > 0 ? (
        <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-8 lg:gap-y-10">
          {visiblePosts.map((post) => (
            <BlogCard key={post.id} post={post} locale={locale} />
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-black/10 bg-white p-8 text-center text-black/60 sm:p-10">
          No blogs found for “{query}”.
        </div>
      )}

      <Pagination className="mt-10 sm:mt-12">
        <PaginationContent className="flex-wrap gap-2">
          <PaginationItem>
            <PaginationPrevious
              href="#"
              onClick={(event) => {
                event.preventDefault();
                goToPage(currentPage - 1);
              }}
              className={cn(
                'h-9 border border-black/10 bg-white px-2 text-xs text-black hover:bg-[#ed1c22] hover:text-white sm:h-10 sm:px-3 sm:text-sm',
                currentPage === 1 && 'pointer-events-none opacity-50',
              )}
            />
          </PaginationItem>

          {Array.from({ length: pageCount }, (_, index) => index + 1).map((pageNumber) => (
            <PaginationItem key={pageNumber}>
              <PaginationLink
                href="#"
                isActive={pageNumber === currentPage}
                onClick={(event) => {
                  event.preventDefault();
                  goToPage(pageNumber);
                }}
                className={cn(
                  'h-9 w-9 border border-black/10 bg-white text-sm text-black hover:bg-[#ed1c22] hover:text-white sm:h-10 sm:w-10',
                  pageNumber === currentPage && 'border-[#ed1c22] bg-[#ed1c22] text-white',
                )}
              >
                {pageNumber}
              </PaginationLink>
            </PaginationItem>
          ))}

          <PaginationItem>
            <PaginationNext
              href="#"
              onClick={(event) => {
                event.preventDefault();
                goToPage(currentPage + 1);
              }}
              className={cn(
                'h-9 border border-black/10 bg-white px-2 text-xs text-black hover:bg-[#ed1c22] hover:text-white sm:h-10 sm:px-3 sm:text-sm',
                currentPage === pageCount && 'pointer-events-none opacity-50',
              )}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </>
  );
}
